/**
 * useNoteAudit Hook
 *
 * Unified hook for audit data management that encapsulates:
 * - Initial data fetching via RTK Query
 * - Optional initial polling when data not yet available
 * - Reaudit triggering with SSE streaming for real-time progress
 * - Derived values (issueCount, sectionIssueMap, cptAnalysis)
 *
 * ARCHITECTURE: RTK Query cache is the SINGLE SOURCE OF TRUTH.
 * During streaming, we write directly to RTK cache. The UI just reads from cache.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEventTracking } from '@services/statsig';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useAppDispatch } from '@store/hooks/core';

import { jotauditApi, useGetNoteAuditDetailQuery, useReauditNoteMutation } from '../store/api';
import type {
  AuditData,
  BillingSupport,
  NoteAuditDetail,
  QualityScores,
  RuleComplianceAssessment,
} from '../types';
import { isValidNoteId } from '../utils/validation';

import { type CptAnalysisData, useCptAnalysis } from './useAuditIssues';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Timeout for SSE streaming (2 minutes)
const STREAM_TIMEOUT_MS = 2 * 60 * 1000;

// Minimum interval between triggerReaudit calls (prevents accidental double-fires
// from double-clicks or multiple trigger paths firing in the same tick).
// Legitimate re-triggers (e.g., CPT change 5s into an audit) will exceed this
// window and go through — the backend handles cancel-and-replace via Redis.
const REAUDIT_DEBOUNCE_MS = 2_000;

// ============================================================================
// TYPES
// ============================================================================

/**
 * Workflow node from manifest (same structure as note generation)
 */
export interface WorkflowNode {
  id: string;
  displayName: string;
  visible?: boolean;
  order: number;
}

interface WorkflowManifest {
  workflowName: string;
  nodes: WorkflowNode[];
  totalSteps: number;
}

/**
 * AG-UI Protocol event types for audit workflow
 */
type AGUIEventType =
  | 'RUN_STARTED'
  | 'RUN_FINISHED'
  | 'STEP_STARTED'
  | 'STEP_FINISHED'
  | 'ACTIVITY_SNAPSHOT'
  | 'completion'
  | 'error';

/**
 * Activity types for ACTIVITY_SNAPSHOT events
 */
type ActivitySnapshotType = 'COMPLIANCE_EVALUATED' | 'QUALITY_SCORED' | 'CPT_ANALYZED';

interface AGUIEvent {
  type: AGUIEventType;
  runId?: string;
  threadId?: string;
  stepName?: string;
  step_id?: string;
  name?: string;
  status?: string;
  message?: string;
  manifest?: WorkflowManifest;
  // ACTIVITY_SNAPSHOT fields
  activityType?: ActivitySnapshotType;
  messageId?: string;
  content?: unknown;
  timestamp?: number;
  replace?: boolean;
}

/**
 * Audit state machine states
 */
export type AuditState =
  | 'idle'
  | 'loading'
  | 'polling_initial'
  /** Polled past `maxDuration` with no audit — the compliance review never landed. */
  | 'expired'
  | 'ready'
  | 'reauditing'
  | 'error';

/**
 * Options for useNoteAudit hook
 */
export interface UseNoteAuditOptions {
  /** Whether the hook is enabled */
  enabled?: boolean;
  /** Polling configuration */
  polling?: {
    /** Initial polling config (for when audit data not yet available) */
    initial?: {
      /** Polling interval in milliseconds */
      interval: number;
      /** Max polling duration before giving up */
      maxDuration: number;
    };
  };
  /** Encounter ID for analytics tracking */
  encounterId?: string;
}

/**
 * Result returned by useNoteAudit hook
 */
export interface UseNoteAuditResult {
  // State
  /** Current state of the audit data */
  state: AuditState;

  // Data - all from RTK Query cache (single source of truth)
  /** Parsed audit data (rule assessments, billing support, scores) */
  data: AuditData | null;
  /** Full audit detail response */
  auditDetail: NoteAuditDetail | undefined;

  // Derived values (computed internally)
  /** Count of non-compliant rules */
  issueCount: number;
  /** Map of section_id to issue count */
  sectionIssueMap: Record<string, number>;
  /** CPT analysis data */
  cptAnalysis: CptAnalysisData | null;

  // Convenience booleans
  /** Whether initial loading or polling */
  isLoading: boolean;
  /** Whether reaudit is in progress */
  isReauditing: boolean;
  /** Whether data is ready */
  isReady: boolean;
  /** Whether any audit data exists */
  hasData: boolean;
  /** Whether audit was generated within user's addon quota (for entitlement gating) */
  withinQuota: boolean;

  // Reaudit streaming state
  /** Current workflow step being executed */
  currentStep: string | null;
  /** List of completed workflow step IDs */
  completedSteps: string[];
  /** Workflow nodes from manifest (for progress display) */
  workflowNodes: WorkflowNode[];

  // Actions
  /** Trigger a reaudit for this note */
  triggerReaudit: () => Promise<void>;
  /** Manually refresh the audit data */
  refresh: () => void;

  // Error
  /** Error message if any */
  error: string | null;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse audit_data JSONB from the response
 */
function parseAuditData(data: NoteAuditDetail | undefined): AuditData | null {
  const rawData = data?.current_revision?.audit_data;
  if (!rawData) return null;

  try {
    const parsed = (
      typeof rawData === 'string' ? JSON.parse(rawData) : rawData
    ) as Partial<AuditData>;
    return {
      rule_compliance_assessments: parsed.rule_compliance_assessments || [],
      billing_support: parsed.billing_support || {
        supports_suggested_cpt: false,
        proposed_primary_code: null,
        confidence: 0,
        strengths: [],
        concerns: [],
        missing_elements_for_code: [],
      },
      scores: parsed.scores || {
        completeness: 0,
        specificity: 0,
        compliance: 0,
        clinical_quality: 0,
      },
    };
  } catch {
    return null;
  }
}

/**
 * Compute issue counts from rule compliance assessments
 * Only counts non-compliant rules (excludes warnings)
 */
function computeAuditIssues(rules: RuleComplianceAssessment[] | null | undefined): {
  issueCount: number;
  sectionIssueMap: Record<string, number>;
} {
  if (!rules) {
    return { issueCount: 0, sectionIssueMap: {} };
  }

  const nonCompliantRules = rules.filter(r => r.compliant === 'no');
  const issueCount = nonCompliantRules.length;

  const sectionIssueMap: Record<string, number> = {};
  nonCompliantRules.forEach(rule => {
    rule.relevant_sections?.forEach(sectionId => {
      sectionIssueMap[sectionId] = (sectionIssueMap[sectionId] || 0) + 1;
    });
  });

  return { issueCount, sectionIssueMap };
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useNoteAudit(
  noteId: string | undefined,
  options?: UseNoteAuditOptions,
): UseNoteAuditResult {
  const { enabled = true, polling, encounterId } = options ?? {};
  const dispatch = useAppDispatch();
  const { noteAudit } = useEventTracking();
  const validNoteId = isValidNoteId(noteId);

  // ============================================================================
  // 1. RTK QUERY - Single source of truth
  // ============================================================================
  const {
    data: auditDetail,
    isLoading: isInitialLoading,
    isFetching,
    refetch,
    error: queryError,
  } = useGetNoteAuditDetailQuery(validNoteId && noteId && enabled ? { noteId } : { noteId: '' }, {
    skip: !validNoteId || !noteId || !enabled,
  });

  // Parse audit_data from RTK Query cache - this IS the data
  const auditData = useMemo(() => parseAuditData(auditDetail), [auditDetail]);

  // Debug: log when auditData changes
  useEffect(() => {
    console.debug('[useNoteAudit] auditData changed:', {
      hasData: !!auditData,
      rulesCount: auditData?.rule_compliance_assessments?.length ?? 0,
      hasScores: !!auditData?.scores,
      hasBillingSupport: !!auditData?.billing_support,
    });
  }, [auditData]);

  // Debug: log when isFetching changes
  useEffect(() => {
    console.debug('[useNoteAudit] isFetching changed:', isFetching);
  }, [isFetching]);

  // CPT analysis from current_revision
  const cptAnalysis = useCptAnalysis(auditDetail?.current_revision);

  // ============================================================================
  // 2. INITIAL POLLING (optional) - Wait for data to exist
  // ============================================================================
  const [pollingState, setPollingState] = useState<'idle' | 'polling' | 'expired'>('idle');
  const pollingIntervalRef = useRef<number | null>(null);
  const pollingTimeoutRef = useRef<number | null>(null);

  // Clear polling when data arrives or noteId changes
  useEffect(() => {
    if (auditData || !noteId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (pollingState === 'polling') {
        setPollingState('idle');
      }
    }
  }, [auditData, noteId, pollingState]);

  // Start polling if configured and no data
  useEffect(() => {
    if (!polling?.initial || auditData || !noteId || !enabled) {
      return;
    }

    if (pollingIntervalRef.current) {
      return;
    }

    setPollingState('polling');

    pollingIntervalRef.current = window.setInterval(() => {
      refetch();
    }, polling.initial.interval);

    pollingTimeoutRef.current = window.setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPollingState('expired');
    }, polling.initial.maxDuration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, enabled, polling?.initial, auditData]);

  // Cleanup polling on unmount or noteId change
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [noteId]);

  // Reset polling state and reaudit debounce when noteId changes
  useEffect(() => {
    setPollingState('idle');
    lastReauditTriggerRef.current = 0;
  }, [noteId]);

  // ============================================================================
  // 3. REAUDIT - SSE streaming that writes directly to RTK cache
  // ============================================================================
  const [reauditNote] = useReauditNoteMutation();

  const [reauditState, setReauditState] = useState<{
    isActive: boolean;
    threadId: string | null;
    currentStep: string | null;
    completedSteps: string[];
    workflowNodes: WorkflowNode[];
    error: string | null;
    startTime: number | null;
    previousIssueCount: number | null;
  }>({
    isActive: false,
    threadId: null,
    currentStep: null,
    completedSteps: [],
    workflowNodes: [],
    error: null,
    startTime: null,
    previousIssueCount: null,
  });

  // Helper to update RTK Query cache directly
  const updateAuditCache = useCallback(
    (updateFn: (draft: NoteAuditDetail) => void) => {
      if (!noteId) return;
      dispatch(
        jotauditApi.util.updateQueryData(
          'getV2ProviderJotauditNotesByNoteId',
          { noteId },
          draft => {
            if (draft) updateFn(draft);
          },
        ),
      );
    },
    [dispatch, noteId],
  );

  // Helper to update audit_data within the cache
  const updateAuditDataInCache = useCallback(
    (updates: Partial<AuditData>) => {
      updateAuditCache(draft => {
        if (!draft.current_revision) return;

        // Parse existing audit_data
        const existing = draft.current_revision.audit_data;
        let parsed: Partial<AuditData> = {};
        try {
          parsed =
            typeof existing === 'string'
              ? (JSON.parse(existing) as Partial<AuditData>)
              : (existing as Partial<AuditData>) || {};
        } catch {
          parsed = {};
        }

        // Merge updates
        draft.current_revision.audit_data = {
          ...parsed,
          ...updates,
        } as AuditData;
      });
    },
    [updateAuditCache],
  );

  // Timestamp of the last triggerReaudit call. Used as a debounce guard to
  // prevent duplicate API calls from double-clicks or multiple trigger paths
  // firing within the same tick (~20ms). Calls after REAUDIT_DEBOUNCE_MS are
  // allowed through for legitimate re-triggers (backend handles cancel-and-replace).
  const lastReauditTriggerRef = useRef(0);

  // Refs for SSE cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cleanupStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  // SSE connection for reaudit streaming
  useEffect(() => {
    if (!reauditState.isActive || !reauditState.threadId) {
      return;
    }

    const threadId = reauditState.threadId;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    streamTimeoutRef.current = setTimeout(() => {
      cleanupStream();

      setReauditState(prev => ({
        ...prev,
        isActive: false,
        error: 'Reaudit timed out',
      }));
    }, STREAM_TIMEOUT_MS);

    const connectToStream = async () => {
      try {
        const token = await getAuth0AccessTokenSilently();
        const streamUrl = `${API_BASE_URL}/v2/provider/jotaudit/stream/${threadId}`;

        await fetchEventSource(streamUrl, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },

          onopen: async response => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Stream connection failed: ${response.status} - ${errorText}`);
            }
          },

          onmessage: event => {
            if (!event.data) return;

            try {
              const data = JSON.parse(event.data) as AGUIEvent;

              switch (data.type) {
                case 'RUN_STARTED':
                  if (data.manifest?.nodes) {
                    const sortedNodes = [...data.manifest.nodes].sort((a, b) => a.order - b.order);
                    setReauditState(prev => ({ ...prev, workflowNodes: sortedNodes }));
                  }
                  break;

                case 'STEP_STARTED': {
                  const stepId = data.step_id || data.stepName || data.name;
                  if (stepId) {
                    setReauditState(prev => ({ ...prev, currentStep: stepId }));
                  }
                  break;
                }

                case 'STEP_FINISHED': {
                  const stepId = data.step_id || data.stepName || data.name;
                  if (stepId) {
                    setReauditState(prev => ({
                      ...prev,
                      completedSteps: [...prev.completedSteps, stepId],
                    }));
                  }
                  break;
                }

                case 'ACTIVITY_SNAPSHOT': {
                  // Write streaming data directly to RTK Query cache
                  switch (data.activityType) {
                    case 'COMPLIANCE_EVALUATED': {
                      const content = data.content as {
                        rule_compliance_assessments?: Array<{
                          rule_id: string;
                          rule_title: string;
                          compliant: string;
                          reason: string;
                          suggestion?: string | null;
                          relevant_sections?: string[] | null;
                        }>;
                      };

                      if (content?.rule_compliance_assessments) {
                        const assessments: RuleComplianceAssessment[] =
                          content.rule_compliance_assessments.map(a => ({
                            rule_id: a.rule_id,
                            rule_title: a.rule_title,
                            compliant: a.compliant as 'yes' | 'no' | 'warning',
                            reason: a.reason,
                            suggestion: a.suggestion ?? null,
                            relevant_sections: a.relevant_sections ?? null,
                          }));

                        console.debug(
                          '[useNoteAudit] COMPLIANCE_EVALUATED: updating cache with',
                          assessments.length,
                          'rules',
                        );
                        updateAuditDataInCache({ rule_compliance_assessments: assessments });
                      }
                      break;
                    }

                    case 'QUALITY_SCORED': {
                      const content = data.content as { scores?: QualityScores };
                      if (content?.scores) {
                        console.debug('[useNoteAudit] QUALITY_SCORED: updating cache with scores');
                        updateAuditDataInCache({ scores: content.scores });
                      }
                      break;
                    }

                    case 'CPT_ANALYZED': {
                      const content = data.content as {
                        recommended_cpt_codes?: string[];
                        cpt_match?: boolean;
                        provider_cpt_codes?: string[];
                        billing_support?: BillingSupport;
                        target_cpt_codes?: string[];
                        target_cpt_source?: string;
                      };

                      if (content) {
                        const billingSupport = content.billing_support ?? {
                          supports_suggested_cpt: false,
                          proposed_primary_code: null,
                          confidence: 0,
                          strengths: [],
                          concerns: [],
                          missing_elements_for_code: [],
                        };

                        updateAuditDataInCache({ billing_support: billingSupport });

                        // Also update CPT fields on current_revision
                        updateAuditCache(draft => {
                          if (draft.current_revision) {
                            draft.current_revision.cpt_analysis_performed = true;
                            draft.current_revision.cpt_match = content.cpt_match ?? false;
                            draft.current_revision.provider_cpt_codes =
                              content.provider_cpt_codes ?? [];
                            draft.current_revision.recommended_cpt_codes =
                              content.recommended_cpt_codes ?? [];
                            // Update new fields if present
                            if (content.target_cpt_codes !== undefined) {
                              draft.current_revision.target_cpt_codes = content.target_cpt_codes;
                            }
                            if (content.target_cpt_source !== undefined) {
                              draft.current_revision.target_cpt_source = content.target_cpt_source;
                            }
                          }
                        });
                      }
                      break;
                    }
                  }
                  break;
                }

                case 'completion':
                  console.debug('[useNoteAudit] SSE completion received, keeping isActive=true until refetch completes');
                  cleanupStream();
                  
                  // Keep isActive true during refetch to prevent flash
                  // Only set isActive=false AFTER refetch completes
                  if (noteId) {
                    console.debug('[useNoteAudit] Calling refetch()');
                    const startTime = reauditState.startTime;
                    const previousIssueCount = reauditState.previousIssueCount;
                    
                    refetch().then((result) => {
                      console.debug('[useNoteAudit] Refetch complete, now setting isActive=false');

                      // Calculate metrics for tracking
                      const duration = startTime ? Date.now() - startTime : 0;

                      // Get new issue count from refetched data
                      let newIssueCount = 0;
                      if (result.data) {
                        const parsedData = parseAuditData(result.data);
                        newIssueCount = parsedData?.rule_compliance_assessments?.filter(
                          r => r.compliant === 'no' || r.compliant === 'warning',
                        ).length ?? 0;
                      }

                      const issuesResolved = previousIssueCount !== null ? previousIssueCount - newIssueCount : undefined;

                      // Track run completed
                      noteAudit.runCompleted({
                        note_id: noteId,
                        encounter_id: encounterId,
                        status: 'success',
                        duration_ms: duration,
                        previous_issue_count: previousIssueCount ?? undefined,
                        new_issue_count: newIssueCount,
                        issues_resolved: issuesResolved,
                      });

                
                      setReauditState(prev => ({
                        ...prev,
                        isActive: false,
                        currentStep: null,
                        startTime: null,
                        previousIssueCount: null,
                      }));
                    });
                  } else {
                    // No noteId, just set inactive
              
                    setReauditState(prev => ({
                      ...prev,
                      isActive: false,
                      currentStep: null,
                      startTime: null,
                      previousIssueCount: null,
                    }));
                  }
                  break;

                case 'error':
                  cleanupStream();

                  // Calculate duration for error tracking
                  const duration = reauditState.startTime ? Date.now() - reauditState.startTime : 0;

                  // Track run completed with error status
                  if (noteId) {
                    noteAudit.runCompleted({
                      note_id: noteId,
                      encounter_id: encounterId,
                      status: 'error',
                      duration_ms: duration,
                      previous_issue_count: reauditState.previousIssueCount ?? undefined,
                    });
                  }

            
                  setReauditState(prev => ({
                    ...prev,
                    isActive: false,
                    error: data.message || 'Reaudit failed',
                    startTime: null,
                    previousIssueCount: null,
                  }));
                  break;
              }
            } catch {
              // Ignore parse errors
            }
          },

          onerror: err => {
            if (controller.signal.aborted) return;
            console.error('[useNoteAudit] SSE error:', err);
            cleanupStream();

            // Calculate duration for error tracking
            const duration = reauditState.startTime ? Date.now() - reauditState.startTime : 0;

            // Track run completed with error
            if (noteId) {
              noteAudit.runCompleted({
                note_id: noteId,
                encounter_id: encounterId,
                status: 'error',
                duration_ms: duration,
                previous_issue_count: reauditState.previousIssueCount ?? undefined,
              });
            }

      
            setReauditState(prev => ({
              ...prev,
              isActive: false,
              error: 'Connection to reaudit stream failed',
              startTime: null,
              previousIssueCount: null,
            }));
          },

          onclose: () => {
            // Stream closed
          },
        });
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[useNoteAudit] Failed to connect to SSE:', err);
          cleanupStream();

          // Calculate duration for error tracking
          const duration = reauditState.startTime ? Date.now() - reauditState.startTime : 0;

          // Track run completed with error
          if (noteId) {
            noteAudit.runCompleted({
              note_id: noteId,
              encounter_id: encounterId,
              status: 'error',
              duration_ms: duration,
              previous_issue_count: reauditState.previousIssueCount ?? undefined,
            });
          }

    
          setReauditState(prev => ({
            ...prev,
            isActive: false,
            error: 'Failed to connect to reaudit stream',
            startTime: null,
            previousIssueCount: null,
          }));
        }
      }
    };

    connectToStream();

    return () => {
      cleanupStream();
    };
  }, [
    reauditState.isActive,
    reauditState.threadId,
    reauditState.startTime,
    reauditState.previousIssueCount,
    noteId,
    encounterId,
    refetch,
    cleanupStream,
    updateAuditCache,
    updateAuditDataInCache,
    noteAudit,
    dispatch,
  ]);

  // Trigger reaudit action
  const triggerReaudit = useCallback(async () => {
    if (!noteId) return;

    // Debounce guard: reject calls within REAUDIT_DEBOUNCE_MS of the last
    // trigger to prevent accidental double-fires (double-click, multiple
    // trigger paths in the same tick). Calls after the window are allowed
    // through — the backend handles cancel-and-replace via Redis.
    const now = Date.now();
    if (now - lastReauditTriggerRef.current < REAUDIT_DEBOUNCE_MS) {
      console.debug('[useNoteAudit] triggerReaudit: skipped (debounce)');
      return;
    }
    lastReauditTriggerRef.current = now;

    console.debug('[useNoteAudit] triggerReaudit: starting');

    // If a reaudit is already in progress, clean up the old SSE stream
    // immediately rather than waiting for the effect cycle. The backend
    // will cancel the old Celery task via its Redis-based guard.
    if (reauditState.isActive) {
      console.debug('[useNoteAudit] triggerReaudit: cancelling in-progress reaudit');
      cleanupStream();
    }

    // Capture current issue count before reaudit
    const currentIssueCount = auditData?.rule_compliance_assessments?.filter(
      r => r.compliant === 'no' || r.compliant === 'warning',
    ).length ?? 0;

    setReauditState({
      isActive: true,
      threadId: null,
      currentStep: null,
      completedSteps: [],
      workflowNodes: [],
      error: null,
      startTime: Date.now(),
      previousIssueCount: currentIssueCount,
    });

    // Reset the cache to empty values (not undefined) so skeletons show
    // but RTK Query doesn't think the data is "missing" and trigger a refetch
    updateAuditCache(draft => {
      if (draft.current_revision) {
        draft.current_revision.audit_data = {
          rule_compliance_assessments: [],
          billing_support: {
            supports_suggested_cpt: false,
            proposed_primary_code: null,
            confidence: 0,
            strengths: [],
            concerns: [],
            missing_elements_for_code: [],
          },
          scores: {
            completeness: 0,
            specificity: 0,
            compliance: 0,
            clinical_quality: 0,
          },
        };
        draft.current_revision.cpt_analysis_performed = false;
        draft.current_revision.cpt_match = undefined;
        draft.current_revision.provider_cpt_codes = [];
        draft.current_revision.recommended_cpt_codes = [];
        draft.current_revision.target_cpt_codes = [];
        draft.current_revision.target_cpt_source = null;
      }
    });

    try {
      const response = await reauditNote({ noteId }).unwrap();
      setReauditState(prev => ({ ...prev, threadId: response.thread_id }));
    } catch (error) {
      console.error('[useNoteAudit] Failed to start reaudit:', error);

      setReauditState(prev => ({
        ...prev,
        isActive: false,
        error: 'Failed to start reaudit',
        startTime: null,
        previousIssueCount: null,
      }));
    }
  }, [noteId, reauditNote, updateAuditCache, auditData, cleanupStream, reauditState.isActive]);

  // ============================================================================
  // 4. DERIVED VALUES
  // ============================================================================
  const { issueCount, sectionIssueMap } = useMemo(
    () => computeAuditIssues(auditData?.rule_compliance_assessments),
    [auditData],
  );

  // ============================================================================
  // 5. STATE DERIVATION
  // ============================================================================
  const state = useMemo((): AuditState => {
    if (!enabled || !noteId) return 'idle';
    if (reauditState.isActive) return 'reauditing';
    if (reauditState.error || queryError) return 'error';
    if (isInitialLoading) return 'loading';
    if (pollingState === 'polling' && !auditData) return 'polling_initial';
    if (auditData) return 'ready';
    // gdn-2614: this was computed and then thrown away — the sidebar spun on
    // skeletons forever. Surface it so the sidebar can say what happened.
    if (pollingState === 'expired') return 'expired';
    return 'idle';
  }, [enabled, noteId, reauditState, queryError, isInitialLoading, pollingState, auditData]);

  // ============================================================================
  // 6. CLEANUP ON UNMOUNT
  // ============================================================================
  useEffect(() => {
    return () => {
      cleanupStream();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [cleanupStream]);

  // ============================================================================
  // 7. RETURN
  // ============================================================================
  return {
    // State
    state,

    // Data - directly from RTK Query cache
    data: auditData,
    auditDetail,

    // Derived values
    issueCount,
    sectionIssueMap,
    cptAnalysis,

    // Convenience booleans
    isLoading: state === 'loading' || state === 'polling_initial' || isFetching,
    isReauditing: state === 'reauditing',
    isReady: state === 'ready',
    hasData: auditData !== null,
    // within_quota from audit.within_quota - defaults to true for legacy audits
    withinQuota: auditDetail?.audit?.within_quota ?? true,

    // Reaudit streaming state
    currentStep: reauditState.currentStep,
    completedSteps: reauditState.completedSteps,
    workflowNodes: reauditState.workflowNodes,

    // Actions
    triggerReaudit,
    refresh: () => {
      void refetch();
    },

    // Error
    error: reauditState.error || (queryError ? 'Failed to load audit data' : null),
  };
}

export default useNoteAudit;
